import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { GARAGE, NAV_LINKS } from "../../lib/constants";
import { useDevis } from "./DevisForm";

export function Logo({ clair = false, surRouge = false, compact = false }) {
  return (
    <Link to="/" data-testid="logo-lien" className="leading-none select-none">
      <span
        className={`font-heading font-bold uppercase tracking-wide transition-all duration-300 ${
          compact ? "text-xl" : "text-2xl"
        } ${surRouge || clair ? "text-white" : "text-brand-dark"}`}
      >
        <span className={surRouge ? "text-brand-dark" : "text-brand-red"}>{"//"}</span>PROMO PNEUS
      </span>
      <span
        className={`block font-heading font-semibold tracking-[0.25em] uppercase mt-0.5 transition-all duration-300 ${
          compact ? "text-[10px]" : "text-xs"
        } ${surRouge ? "text-white" : "text-brand-red"}`}
      >
        Neufs et occasions
      </span>
    </Link>
  );
}

export default function Header({ bandeau, fermeture, catalogueVisible = true }) {
  const [ouvert, setOuvert] = useState(false);
  const [compact, setCompact] = useState(false);
  const { openDevis } = useDevis();
  const location = useLocation();
  const liens = catalogueVisible ? NAV_LINKS : NAV_LINKS.filter((l) => l.to !== "/catalogue");

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {bandeau && (
        <div data-testid="bandeau-promotion" className="bg-brand-red text-white border-b border-white/25">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-center gap-3">
            <p className="font-heading font-semibold uppercase tracking-wide text-sm truncate min-w-0">
              {bandeau.texte_bandeau || bandeau.titre}
            </p>
            <button
              data-testid="bandeau-promotion-bouton"
              onClick={() => openDevis()}
              className="shrink-0 bg-white text-brand-red font-heading font-semibold uppercase text-xs px-3 py-1 hover:bg-brand-dark hover:text-white transition-colors"
            >
              J'en profite
            </button>
          </div>
        </div>
      )}
      {fermeture && (
        <div
          data-testid="bandeau-fermeture"
          className="bg-brand-navy text-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 text-center">
            <p className="font-heading font-semibold uppercase tracking-wide text-sm sm:text-base">
              Fermeture exceptionnelle du {fermeture.debut.split("-").reverse().join("/")} au{" "}
              {fermeture.fin.split("-").reverse().join("/")}
              {fermeture.motif ? ` — ${fermeture.motif}` : ""}
            </p>
          </div>
        </div>
      )}

      <header className="bg-brand-red sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${compact ? "h-14" : "h-20"}`}>
            <Logo surRouge compact={compact} />
            <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
              {liens.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/é/g, "e")}`}
                  className={({ isActive }) =>
                    `font-heading font-semibold uppercase tracking-wider text-base transition-colors ${
                      isActive ? "text-brand-dark" : "text-white hover:text-brand-dark"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:${GARAGE.telephoneLien}`}
                data-testid="nav-telephone"
                className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-base text-white hover:text-brand-dark transition-colors"
              >
                <Phone size={16} aria-hidden="true" />
                {GARAGE.telephone}
              </a>
              <button
                data-testid="nav-demander-devis"
                onClick={() => openDevis()}
                className="bg-brand-dark text-white font-heading font-semibold uppercase tracking-wider px-6 py-3 hover:bg-black transition-colors"
              >
                Demander un devis
              </button>
            </nav>
            <div className="flex items-center lg:hidden">
              <a
                href={`tel:${GARAGE.telephoneLien}`}
                data-testid="header-appeler-mobile"
                aria-label="Appeler le garage"
                className="text-white p-3"
              >
                <Phone size={26} aria-hidden="true" />
              </a>
              <button
                data-testid="menu-mobile-bouton"
                className="text-white p-3 -mr-2"
                onClick={() => setOuvert(!ouvert)}
                aria-label={ouvert ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={ouvert}
              >
                {ouvert ? <X size={30} /> : <Menu size={30} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {ouvert && (
            <motion.nav
              key={location.pathname}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden overflow-hidden bg-brand-dark"
              aria-label="Navigation mobile"
            >
              <div className="px-4 py-4 flex flex-col">
                {liens.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOuvert(false)}
                    className={({ isActive }) =>
                      `font-heading font-semibold uppercase tracking-wider text-xl py-3.5 border-b border-white/10 transition-colors ${
                        isActive ? "text-brand-red" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <button
                  data-testid="menu-mobile-devis"
                  onClick={() => {
                    setOuvert(false);
                    openDevis();
                  }}
                  className="mt-4 bg-brand-red text-white font-heading font-semibold uppercase tracking-wider text-xl py-4"
                >
                  Demander un devis
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
